import { Text } from 'pixi.js'
import { Shape } from "../../helpers/Shape";
import { Row } from './Row'
import { TYPES } from "../../EntityManager";
import { graphic } from "../../Graphic";
import { EntityService } from "../EntityService";
import { Entity } from "../Entity";
import { selection } from "../../editions/selection/Selection";

export class RowService extends EntityService {

    static upsertFromProps(rowsProps) {
        super.upsertFromProps(TYPES.ROW, rowsProps);
    }

    static createFromProps(props) {
        const { id, container } = super.createFromProps(props);
        const { label, category, curve, sequence, sequenceOffset, labelDisplay } = props;

        const backgroundColor = category
            ? `0x${category.color.replace('#', '')}`
            : Row.defaultBackgroundColor;

        const graphicItems = RowService.createGraphicItems(parseInt(props.length), backgroundColor);
        return new Row({ id, label, container, graphicItems, category, curve, sequence, sequenceOffset, labelDisplay });
    }

    static generateIndex(label) {
        let value = 0;

        for(let i = 1; i < label.length; i++) {
            value += Math.pow(26, i);
        }
        value += (label.charCodeAt(0) - 65);
        for(let i = 1; i < label.length; i++) {
            value += (label.charCodeAt(i) - 65) * Math.pow(26, i)
        }

        return value
    }

    static generateLabel(index) {
        let suffix = index >= 26
            ? RowService.generateLabel((index / 26 >> 0) - 1)
            : '';
        let letter = 'abcdefghijklmnopqrstuvwxyz'[index % 26 >> 0];

        return `${suffix}${letter}`.toUpperCase();
    }

    static changeLabelMultipleRows({ rows, value }) {
        if (!value.trim()) return;

        let blockLabel = null;
        if (value.indexOf('-') !== -1) {
            blockLabel = value.split('-')[0];
            value = value.split('-')[1];
        }

        value = value.split('').reverse().join('');

        if (value[0] >= '0' && value[0] <= '9') {
            let number = '';
            let i = 0;
            while (value[i] && value[i] >= '0' && value[i] <= '9') {
                number += value[i];
                i++;
            }
            value = value.replace(number, '')
            number = number.split('').reverse().join('');
            rows.forEach(row => row.label.setValue(
                blockLabel
                    ? `${blockLabel}-` + value + number++
                    : value + number++
            ));
        } else {
            let labelIndex = RowService.generateIndex(value.toUpperCase());
            rows.forEach(row => {
                const generatedLabel = RowService.generateLabel(labelIndex);
                row.label.setValue(
                    blockLabel
                        ? `${blockLabel}-` + generatedLabel
                        : generatedLabel
                );
                labelIndex++;
            });
        }
    }

    static changeLabel({ rowsId, value }) {
        rowsId.forEach(rowId => graphic.entityManager.get(TYPES.ROW, rowId).label.setValue(value));
    }

    static changeSequenceType({ rowsId, value }) {
        rowsId.forEach(rowId => graphic.entityManager.get(TYPES.ROW, rowId).sequence.setType(value));
    }

    static changeSequenceOffset({ rowsId, value }) {
        rowsId.forEach(rowId => graphic.entityManager.get(TYPES.ROW, rowId).sequence.setOffset(value));
    }

    static changeSeatsCount({ rowsId, value }) {
        rowsId.forEach(rowId => graphic.entityManager.get(TYPES.ROW, rowId).setSeatsCount(value));
        selection.update();
    }

    static changeCurve({ rowsId, value }) {
        rowsId.forEach(rowId => graphic.entityManager.get(TYPES.ROW, rowId).curve.setValue(value));
    }

    static changeCategory({ rows, category }) {
        rows.forEach(row => row.setCategory(category));
    }

    static setSeatText({ row, index, text }) {
        row.seatText = new Text(text, {
            fontSize: 18
        });
        row.container.addChild(row.seatText);
        row.seatText.zIndex = 1000;

        const firstGraphicBounds = row.graphicItems[0].getLocalBounds();
        const offsetLeft = firstGraphicBounds.x;

        const graphicBounds = row.graphicItems[index].getLocalBounds();
        row.seatText.x = graphicBounds.x - (row.seatText.width / 2) - offsetLeft;
        row.seatText.y = -row.seatText.height * 3;

        const backgroundMargin = 15;
        row.seatTextBackground = Shape.createRectangle(
            row.seatText.x - backgroundMargin,
            row.seatText.y - backgroundMargin,
            row.seatText.x + row.seatText.width + backgroundMargin,
            row.seatText.y + row.seatText.height + backgroundMargin,
            [2, 0x000000, 0],
            0xFFFFFF,
            1
        );
        row.container.addChildAt(row.seatTextBackground, 0);
    }

    static removeSeatText({ row, index }) {
        row.container.removeChild(row.seatText);
        row.container.removeChild(row.seatTextBackground);
    }

    static toggleShowLabels() {
        Row.showLabels = !Row.showLabels;
        graphic.entityManager.forEach(TYPES.ROW, row => {
            if (Row.showLabels) {
                row.label.draw();
            } else {
                row.label.destroy();
            }
        });
    }

    static createGraphicItem(x, y, backgroundColor = Row.defaultBackgroundColor) {
        return Shape.createCircle(x, y, Row.size, [backgroundColor, 1], [1, 0xFFFFFF, 0.1, 1]);
    }

    static createGraphicItems(count, backgroundColor) {
        const graphicItems = [];
        while(count  !== 0) {
            if (Math.sign(count) === 1) count--;
            else if (Math.sign(count) === -1) count++;

            const x = (count * (Row.size + Row.margin) * 2);
            const y = 0;
            const graphicItem = RowService.createGraphicItem(x, y, backgroundColor);
            graphicItem.status = Entity.GRAPHIC_STATE_FREE;
            graphicItems.push(graphicItem);
        }
        return graphicItems.reverse();
    }
}